import {
    BoxedRow,
    ButtonFixedFooterLayout,
    ButtonPrimary,
    Callout,
    IconInformationUserRegular,
    IconShieldCrossRegular,
    NavigationBar,
    RadioGroup,
    skinVars,
    Text3,
    Text6,
    useWindowSize,
} from '@telefonica/mistica';
import HeaderComponent from '../../../../components/HeaderComponent';
import { t } from 'i18next';
import { formatMobileNumber } from '../../../../utils/dataTransformation/unitsConversion';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { getContactMedia, startChangeOwnership } from '../../../../services/OwnershipChangeService';
import { changeOwnershipStartRequest, OwnerContacts } from '../../../../models/OwnershipChange';
import { useQuery } from '@tanstack/react-query';
import { useChangeOwnershipContext } from '../../../../context/change-ownership-context/ChangeOwnershipContext';
import './index.scss';
import { getCustomerData } from '../../../../services/CustomerService';

const ContactSelectionContractSingning = () => {
    const navigate = useNavigate();
    const size = useWindowSize();
    const [contacts, setContacts] = useState<OwnerContacts[]>([]);
    const {
        dataChangeOwnership,
        selectedProduct,
        partyRoleId,
        partyRoleType,
        owner,
        contactMediaOTP,
        setContactMediaOTP,
        isOpenOnboarding,
    } = useChangeOwnershipContext();
    const [autoSelect, setAutoSelect] = useState(false);
    const { data: personalData } = useQuery({ queryKey: ['customer-data'], queryFn: getCustomerData });
    const escalation = localStorage.getItem('escalation');
    const escalationData = JSON.parse(escalation!);
    const isReceptor =
        dataChangeOwnership?.interested?.interestedId &&
        personalData?.data.id === dataChangeOwnership?.interested?.interestedId;
    const selectedContact = contacts.find((contact) => contact.value === contactMediaOTP);

    useEffect(() => {
        (async () => await getContacts())();
    }, []);

    useEffect(() => {
        if (autoSelect && contactMediaOTP) {
            handleButtonContinue();
            setAutoSelect(false);
        }
    }, [contactMediaOTP, autoSelect]);

    const getContacts = async () => {
        try {
            const response = await getContactMedia();

            if (response.data.length === 0) {
                navigate('/change-ownership/feedback?status=NO_VERIFIED_CONTACT');
                return;
            } else if (response.data.length === 1) {
                setContacts(response.data);
                setContactMediaOTP(response.data[0].value);
                setAutoSelect(true);
            } else {
                setContacts(response.data);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleButtonContinue = async () => {
        if (isReceptor) {
            if (selectedContact?.value) {
                setContactMediaOTP(selectedContact.value);
            }
            navigate('/change-ownership/continue');
            return;
        }
        const data: changeOwnershipStartRequest = {
            contactSessionId: escalationData?.contactSessionId,
            products: [
                {
                    productId: selectedProduct.productId,
                    productOfferingId: selectedProduct.productOfferingId,
                },
            ],
            contactMediaOTP,
            partyRoleId,
            partyRoleType,
            signalingData: {
                name: owner.name,
                firstSurname: owner.firstSurname,
                secondSurname: owner.secondSurname,
                documentType: owner.documentType,
                documentNumber: owner.documentNumber,
                contactMedia: owner.contactPhone,
                url: isOpenOnboarding
                    ? `${window.location.origin + '/customer-verification-no-client?signalingId='}`
                    : undefined,
            },
        };
        try {
            await startChangeOwnership(data);
            navigate('/change-ownership/feedback?status=START');
        } catch {
            navigate('/change-ownership/feedback?status=FAILED_PETITIONER');
        }
    };

    const navigationBackHandler = () => navigate(-1);

    const mainBody = () => {
        return (
            <ButtonFixedFooterLayout
                button={
                    <ButtonPrimary disabled={!contactMediaOTP} onPress={() => handleButtonContinue()}>
                        {t('buttonReceiveCode')}
                    </ButtonPrimary>
                }
            >
                <div className='contact-selection-signing__description'>
                    <Text3 regular color={skinVars.colors.textPrimary}>
                        {t('changeOwnerContactMediaDescription')}
                    </Text3>
                </div>
                <div className='contact-selection-signing__radioGroup'>
                    <RadioGroup
                        name={'verification'}
                        value={contactMediaOTP}
                        onChange={(value) => {
                            const contact = contacts.find((item) => item.value === value);
                            setContactMediaOTP(contact?.value ?? '');
                        }}
                    >
                        {contacts?.map((contact: OwnerContacts) => {
                            return (
                                <div className='contact-selection-signing__boxedRow' key={contact.id}>
                                    <BoxedRow
                                        title={t('personalDataMobile')}
                                        description={formatMobileNumber(contact.value)}
                                        radioValue={contact.value}
                                    ></BoxedRow>
                                </div>
                            );
                        })}
                    </RadioGroup>
                </div>
                <div className='contact-selection-signing__callout'>
                    <Callout
                        asset={<IconInformationUserRegular size={24} color={skinVars.colors.brand} />}
                        description={t('changeOwnerContactMediaCallout')}
                    ></Callout>
                </div>
            </ButtonFixedFooterLayout>
        );
    };

    return (
        <div className='contact-selection-signing'>
            <div className='contact-selection-signing__header'>
                {size.width > 1024 ? (
                    <NavigationBar />
                ) : (
                    <HeaderComponent
                        title={t('selectContactMedia')}
                        navigationBarOnBack={navigationBackHandler}
                        breadcrumbs={[]}
                    />
                )}
            </div>
            <div className='contact-selection-signing__content'>
                {size.width > 1024 ? (
                    <>
                        <div className='contact-selection-signing__left'>
                            <div className='contact-selection-signing__left-icon'>
                                <IconShieldCrossRegular
                                    size={64}
                                    color={skinVars.colors.brand}
                                ></IconShieldCrossRegular>
                            </div>
                            <div className='contact-selection-signing__left-title'>
                                <Text6 color={skinVars.colors.textPrimary}>{t('otpFlowTitle')}</Text6>
                            </div>
                            <div className='contact-selection-signing__left-subtitle'>
                                <Text3 regular color={skinVars.colors.textSecondary}>
                                    {t('otpFlowSubtitle')}
                                </Text3>
                            </div>
                        </div>
                        <div className='contact-selection-signing__right'>{mainBody()}</div>
                    </>
                ) : (
                    mainBody()
                )}
            </div>
        </div>
    );
};

export default ContactSelectionContractSingning;
